import { IDs } from '../../../helpers/IDs';
import { ImportConstants } from '../ImportConstants';
import * as Moment from 'moment';

export abstract class Aufnahmebogen {

    protected readonly colEnd: number;
    protected readonly colGap: number = 4;
    protected readonly colStart: number;
    protected curDataIndex: number = 0;
    protected curEntity: string;
    protected curRowIndex: number;
    protected data: any = {};
    protected readonly dataDb: any;
    protected readonly dataSheet: Array<any>;
    abstract getData(): Array<any>;

    constructor(dataSheet: Array<any>, dataDb?: any) {
        this.dataSheet = dataSheet || [];
        this.dataDb = dataDb || {};
        this.colEnd = (+ImportConstants.COL_AUFNAHME_EINHEIT || 0) - 1;
        this.colStart = (+ImportConstants.COL_AUFNAHME_TITEL || 0) + this.colGap;
    }

    protected callTuples(index: number, callback: (values: Array<any>, index?: number) => void): void {
        const entity: string = this.curEntity || null;

        if (!this.data[entity]) {
            if (typeof callback === 'function') {
                callback([], 0);
            }
            return;
        }
        const data: Array<any> = this.dataSheet[index] || [];
        const obj: any = this;
        let n: number = this.curDataIndex;

        const values = function* (start: any) {
            for (let value = start; value < (start + obj.colGap); ++value) {
                yield data[value];
            }
        };

        for (let i = this.colStart; i <= this.colEnd; i += obj.colGap) {
            if (typeof callback === 'function') {
                if (typeof this.data[entity][n] === 'undefined') {
                    this.data[entity][n] = {};
                }
                callback([...Array.from(values(i))], n++);
            }
        }
    }

    protected callValues(index: number, callback: (value: any, index?: number) => void): void {
        if (typeof callback === 'function') {
            this.callTuples(index, (values: Array<any>, n: number) => {
                callback(values[0] || (typeof values[0] === 'number' ? 0 : null), n);
            });
        }
    }

    protected getBemerkung(index?: number): string {
        const colBemerkung: number = this.curEntity === IDs.ENT_SONSTIGE_DATEN
            ? ImportConstants.COL_AUFNAHME_BEMERKUNG_BERATER
            : ImportConstants.COL_AUFNAHME_BEMERKUNG;
        const curRowIndex: number = typeof index === 'undefined'
            ? this.curRowIndex
            : index;

        return ((this.dataSheet[curRowIndex] || [])[colBemerkung] || '').trim();
    }

    protected getDate(value: number): number {
        return +new Date(((+value - (25567 + 2)) * 86400 * 1000));
    }

    protected getDateDiff(values: [number, number]): number {
        if (+values[0] <= 0 || +values[1] <= 0) {
            return null;
        }

        return Moment(this.getDate(values[1])).diff(this.getDate(values[0]), 'd') + 1;
    }

    protected getDateUnix(value: number): number {
        return value ? Moment(this.getDate(value)).unix() : null;
    }

    protected getEinheit(index?: number): string {
        const curRowIndex: number = typeof index === 'undefined'
            ? this.curRowIndex
            : index;

        return ((this.dataSheet[curRowIndex] || [])[ImportConstants.COL_AUFNAHME_EINHEIT] || '').trim();
    }

    protected getTitel(index?: number): string {
        const curRowIndex: number = typeof index === 'undefined'
            ? this.curRowIndex
            : index;

        return ((this.dataSheet[curRowIndex] || [])[ImportConstants.COL_AUFNAHME_TITEL] || '').trim();
    }

    protected initData(name: string, index: number = 0): void {
        const factor: number = Math.ceil((this.colEnd - this.colStart) / this.colGap);
        this.curDataIndex = index * factor;

        if (!this.data[name]) {
            this.curDataIndex = index || 0;
            this.curEntity = name;
            this.data = { ...this.data, [name]: [] };
        }
    }

    protected isHeaderRow(data: Array<string>): boolean {
        return (
            data.length === 3
            && (data[ImportConstants.COL_AUFNAHME_TITEL] || null) !== 'Rechnung vorhanden'
            && (data[ImportConstants.COL_AUFNAHME_TITEL] || null) !== 'Rechnungen/Belege vorhanden'
            && (data[ImportConstants.COL_AUFNAHME_TITEL] || null) !== 'Separate Abrechn. E-Mobil-Strom'
        );
    }

    protected setAbrechnungszeitraum(): void {
        const entity: string = this.curEntity || null;

        if (typeof this.curRowIndex !== 'number' || !this.data[entity]) {
            return;
        }

        this.callTuples(this.curRowIndex, (values: Array<any>, index: number) => {
            const bis: number = values[2] || null;
            const von: number = values[0] || null;
            this.data[entity][index] = {
                ...this.data[entity][index],
                abrechnungstage: this.getDateDiff([von, bis]),
                abrechnungszeitraum_bis: this.getDateUnix(bis),
                abrechnungszeitraum_von: this.getDateUnix(von)
            };
        });
    }

    protected setBemerkung(): void {
        const name: string = 'bemerkung';
        const bemerkung: string = this.getBemerkung() || '';
        const bemerkungen: string = this.data[this.curEntity][this.curDataIndex][name] || '';

        this.setValue(name, (value: any, index: number) => {
            if (bemerkungen.length || bemerkung.length) {
                this.data[this.curEntity][index][name] = [bemerkungen, bemerkung]
                    .join(bemerkungen.length && bemerkung.length ? '; ' : '');
            }
        });
    }

    protected setJahre(): void {
        const curRowIndex: number = this.curRowIndex;
        this.curRowIndex = 0;
        this.setValue('jahr');
        this.curRowIndex = curRowIndex;
    }

    protected setValue(name: string, callback?: (value: any, index?: number) => void): void {
        const entity: string = this.curEntity || null;

        if (typeof this.curRowIndex !== 'number' || !this.data[entity]) {
            return;
        }

        this.callValues(this.curRowIndex, (value: number | string, index: number) => {
            if (typeof callback === 'function') {
                callback(value, index);
            } else if (value !== null && value.toString().length) {
                this.data[entity][index][name] = value;
            }
        });
    }
}
